// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface HeroLayout1Data {
  title?: string;
  subtitle?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: any;
}

/**
 * Preview component for "Hero Layout 1" used by the website builder — renders a hero section (image, title, body, CTAs) with configurable typography and colors and emits button click events.
 * @component
 * @selector app-hero-layout-1-preview
 * @example
 *   <app-hero-layout-1-preview [data]="heroData" [headingColor]="'#1a1a1a'" (buttonClick)="onHeroButtonClick($event)"></app-hero-layout-1-preview>
 * @input {HeroLayout1Data | null} data - Main content payload for the hero (title, subtitle, image object or imageUrl, buttons, image scaletype, etc.). Can be null when no data is available.
 * @input {string | null} headingColor - Hex or CSS color value applied to the heading text.
 * @input {string | null} paragraphColor - Hex or CSS color value applied to paragraph/body text.
 * @input {string | null} headingFont - Font family or font identifier used for the heading.
 * @input {string | null} bodyFont - Font family or font identifier used for the body/paragraph text.
 * @input {string | null} primaryColor - Primary accent color used for CTAs and accent elements.
 * @input {any} titleStyles - Optional inline style object applied to the title element (allows custom style overrides).
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when a button/CTA is clicked. Payload includes the target link (if present) and the originating DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void
 *     Emits the buttonClick event only when a valid link is provided. Used as the click handler for CTA buttons.
 * - getImageSrc(): string
 *     Resolves the image source from data.image.src or falls back to data.imageUrl; returns empty string if none.
 * - getImageAlt(): string
 *     Resolves the image alt text from data.image.alt or returns a sensible default ('Car Wash').
 * - getObjectFit(): string
 *     Maps the data image scaletype ('fill' | 'fit' | 'stretch') to the corresponding CSS object-fit value ('cover' | 'contain' | 'fill').
 * - getImageStyles(): any
 *     Builds and returns an inline style object for the image element (currently sets object-fit based on scaletype).
 *
 * Lifecycle hooks:
 * - This class does not implement Angular lifecycle hooks explicitly (OnInit/OnChanges) — it relies on reactive inputs. If consumers need to react to input changes with side effects, implementing OnChanges or OnInit is recommended.
 */
@Component({
  selector: 'app-hero-layout-1-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-1.component.scss'],
})
export class HeroLayout1PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  /**
   * Determine the appropriate CSS object-fit value from this.data.image.scaletype ('fit'|'fill'|'stretch'), defaulting to 'contain'.
   * @example
   * etObjectFit()
   * 'contain'
   * @param {void} none - No parameters.
   * @returns {string} CSS object-fit value ('contain' for fit/default, 'cover' for fill, 'fill' for stretch).
   **/
  getObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'fit';
    // Map scale types to CSS object-fit values
    switch (scaletype) {
      case 'fill':
        return 'cover'; // Fill: fills container, maintains aspect ratio, may crop
      case 'fit':
        return 'contain'; // Fit: fits within container, maintains aspect ratio, no cropping
      case 'stretch':
        return 'fill'; // Stretch: stretches to fill container exactly, may distort
      default:
        return 'contain';
    }
  }

  /**
   * Compute and return the inline CSS styles for the hero image, primarily setting the 'object-fit' based on component data.
   * @example
   * etImageStyles()
   * { 'object-fit': 'fit' }
   * @returns {{any}} Inline style object for the image (e.g., contains an 'object-fit' property).
   **/
  getImageStyles(): any {
    const baseStyles: any = {
      'object-fit': this.getObjectFit(),
    };

    // For all types, the image should fill its container
    // The CSS already sets width: 100% and height: 100% on .car-illustration
    // The difference is in object-fit behavior

    return baseStyles;
  }
}
